<template>
  <div class="compare">
    <div class="compare-top">
      <v-btn icon aria-label="Back to my builds" @click="$emit('back')"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <span class="collection-eyebrow">SIDE BY SIDE</span>
      <v-btn icon aria-label="Swap builds" @click="$emit('swap')"><v-icon>mdi-swap-horizontal</v-icon></v-btn>
      <v-btn icon aria-label="Close comparison" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </div>
    <h2 id="collection-title" class="compare-title">Compare builds.</h2>
    <div class="compare-cards">
      <div v-for="side in sides" :key="side.letter" class="compare-card" :class="'is-' + side.letter.toLowerCase()">
        <span class="compare-letter">{{ side.letter }}</span>
        <strong>{{ side.build.name }}</strong>
        <span class="compare-total">{{ money(side.total.price) }}</span>
        <span class="compare-weight">{{ side.total.weight ? `≈${kg(side.total.weight.grams)} kg est.` : 'Weight not available' }}</span>
        <button v-if="!side.build.current" type="button" class="compare-load" @click="$emit('load', side.build)">Load build <span aria-hidden="true">→</span></button>
        <span v-else class="compare-load is-current">On screen now</span>
      </div>
    </div>
    <p class="compare-headline" role="status"><v-icon small color="white">mdi-scale-balance</v-icon><span>{{ headline }}</span></p>
    <div class="compare-bar">
      <span>{{ result.differences }} of {{ result.rows.length }} parts differ</span>
      <button v-if="result.differences && result.differences < result.rows.length" type="button" @click="onlyDifferences = !onlyDifferences">{{ onlyDifferences ? 'Show all parts' : 'Only differences' }}</button>
    </div>
    <ul class="compare-rows">
      <li v-for="row in rows" :key="row.id" class="compare-row" :class="{ 'is-different': !row.same }">
        <div class="compare-row-head">
          <span class="compare-label">{{ row.label }}</span>
          <span class="compare-delta" :class="{ 'is-same': row.same }">{{ row.same ? 'Same' : row.delta }}</span>
        </div>
        <div v-if="row.same" class="compare-part is-shared">
          <span class="compare-part-name">{{ row.a.name }}<small v-if="row.a.detail">{{ row.a.detail }}</small></span>
          <span class="compare-part-price">{{ money(row.a.price) }}</span>
        </div>
        <div v-else class="compare-sides">
          <div v-for="side in ['a', 'b']" :key="side" class="compare-part">
            <span class="compare-letter is-small" :class="'is-' + side">{{ side.toUpperCase() }}</span>
            <span v-if="row[side]" class="compare-part-name">{{ row[side].name }}<small v-if="row[side].detail">{{ row[side].detail }}</small></span>
            <span v-else class="compare-part-name is-empty">Not included</span>
            <span class="compare-part-price">{{ row[side] ? money(row[side].price) : '—' }}</span>
          </div>
        </div>
      </li>
    </ul>
    <p class="compare-note">Prices are SRP. Weights are estimates from manufacturer-claimed figures.</p>
  </div>
</template>

<script>
import { kg } from '@/utils/weight';
import { compareBuilds, compareSummary, money } from '@/utils/compareBuilds';
export default {
  props: { left: Object, right: Object },
  data: () => ({ onlyDifferences: false }),
  computed: {
    result() { return compareBuilds(this.left.selection, this.right.selection); },
    sides() { return [{ letter: 'A', build: this.left, total: this.result.a }, { letter: 'B', build: this.right, total: this.result.b }]; },
    headline() {
      if (!this.result.differences) return 'These builds are identical.';
      return compareSummary(`B (${this.right.name})`, this.result, 'A') + (this.result.weightGaps ? '. Weight not comparable: a changed part has no published weight.' : '.');
    },
    rows() { return this.onlyDifferences ? this.result.rows.filter(row => !row.same) : this.result.rows; }
  },
  methods: { kg, money }
};
</script>

<style lang="scss">
.compare-top { position: sticky; top: 0; z-index: 2; display: flex; align-items: center; gap: 2px; margin: -16px -16px 8px; padding: 4px 0; background: #fff; }
.compare-top .collection-eyebrow { flex: 1; margin: 0 0 0 6px; }
.compare-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
.compare-card { display: flex; flex-direction: column; min-width: 0; padding: 14px; border-radius: 12px; background: #f4f5f0; border: 1px solid #e9ece4; }
.compare-card.is-b { background: #edf0e7; }
.compare-card strong { font-size: 13px; line-height: 1.3; margin: 8px 0 6px; overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.compare-total { font-size: 19px; font-weight: 600; letter-spacing: -.02em; color: #344b35; }
.compare-weight { font-size: 11px; color: #7b8375; margin-top: 2px; }
.compare-load { margin-top: auto; padding-top: 10px; text-align: left; font-size: 12px; font-weight: 700; color: #344638; }
.compare-load.is-current { font-weight: 400; color: #7b8375; }
.compare-letter { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 7px; font-size: 11px; font-weight: 700; color: #fff; background: #73816d; flex: none; }
.is-b > .compare-letter, .compare-letter.is-b { background: #344638; }
.compare-letter.is-small { width: 20px; height: 20px; border-radius: 6px; font-size: 10px; }
.compare-letter.is-a { background: #73816d; }
.collection-content p.compare-headline { display: flex; gap: 10px; align-items: flex-start; padding: 14px 16px; border-radius: 12px; background: #344638; color: #fff; font-size: 14px; font-weight: 600; line-height: 1.45; margin-bottom: 18px; }
.compare-headline .v-icon { margin-top: 2px; }
.compare-bar { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #73816d; padding-bottom: 8px; border-bottom: 1px solid #e9ece4; }
.compare-bar button { text-transform: none; letter-spacing: 0; color: #344638; font-size: 12px; text-decoration: underline; }
.compare-rows { list-style: none; padding: 0 !important; margin: 0 0 14px; }
.compare-row { padding: 12px 0; border-bottom: 1px solid #e9ece4; }
.compare-row.is-different { margin: 6px -10px; padding: 12px 10px; border: 0; border-left: 3px solid #344b35; border-radius: 10px; background: #f4f5f0; }
.compare-row-head { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; margin-bottom: 6px; }
.compare-label { font-size: 10px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #73816d; }
.compare-delta { font-size: 12px; font-weight: 700; color: #344b35; white-space: nowrap; font-variant-numeric: tabular-nums; }
.compare-delta.is-same { font-weight: 400; color: #9aa293; }
.compare-sides { display: grid; gap: 6px; }
.compare-part { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 1.35; }
.compare-part-name { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.compare-part-name small { display: block; font-size: 11px; color: #7b8375; margin-top: 1px; }
.compare-part-name.is-empty { color: #9aa293; font-style: italic; }
.compare-part.is-shared { color: #697069; }
.compare-part-price { white-space: nowrap; font-variant-numeric: tabular-nums; color: #4b5348; }
.collection-content p.compare-note { font-size: 11px; color: #858b80; margin: 0; }
@media(min-width: 600px) {
  .compare-sides { grid-template-columns: 1fr 1fr; gap: 14px; }
}
@media(max-width: 599px) {
  .collection-content h2.compare-title { font-size: 24px; margin-bottom: 12px; }
}
</style>
